import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Input, Typography } from "@lucid/lego";
import {
  SOURCE_STORAGE_KEY,
  storeSourceUrl,
  useBackNavigation,
} from "@/hooks/useBackNavigation";
import { ROUTES } from "@/routes/routes";
import { ENV_APP_BASE_URL } from "@/env";
import {
  DropdownMenu,
  type DropdownMenuItem,
} from "@/components/DropdownMenu/DropdownMenu";
import {
  setDashboardSearchValue,
  useDashboardSearchValue,
} from "@/hooks/useDashboardSearch";
import { stripLocalePrefixFromPath } from "@/utils";
import { hasOrgAdminRole } from "@/auth0/accessControl";
import lucidBearLogo from "@/assets/bear_logo.svg.svg";
import lucidWordmarkLogo from "@/assets/lucid_logo.svg.svg";
import "./Header.css";

interface HeaderProps {
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  orgAdminRoles: string[];
}

export default function Header({
  firstName,
  lastName,
  organizationName = "",
  orgAdminRoles = [],
}: HeaderProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth0();
  const { goBack, canGoBack } = useBackNavigation();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const searchValue = useDashboardSearchValue();
  const normalizedPathname = stripLocalePrefixFromPath(pathname, lang);
  const isDashboardRoute = normalizedPathname === ROUTES.DASHBOARD;
  const isProfileSettingsRoute = normalizedPathname === ROUTES.PROFILE_SETTINGS;
  const isMfaRoute = normalizedPathname === ROUTES.MFA;
  const isUpdatePhoneRoute = normalizedPathname === ROUTES.UPDATE_PHONE;
  const isUserManagementRoute = normalizedPathname === ROUTES.USER_MANAGEMENT;
  const canAccessUserManagement = hasOrgAdminRole(user, orgAdminRoles);

  const profileSettingsPath = lang
    ? `/${lang}${ROUTES.PROFILE_SETTINGS}`
    : ROUTES.PROFILE_SETTINGS;

  const userManagementPath = lang
    ? `/${lang}${ROUTES.USER_MANAGEMENT}`
    : ROUTES.USER_MANAGEMENT;

  const dropdownUserName =
    `${firstName ?? ""} ${lastName ?? ""}`.trim();

  useEffect(() => {
    if (!isDashboardRoute) {
      setDashboardSearchValue("");
    }
  }, [isDashboardRoute]);

  const handleSearchChange = (value: string) => {
    if (!isDashboardRoute) {
      return;
    }

    setDashboardSearchValue(value.slice(0, 255));
  };

  const handleMenuItemClick = (targetPath: string) => {
    // Only store the source URL if one doesn't already exist in sessionStorage
    // This preserves the original source across multiple navigations
    if (!sessionStorage.getItem(SOURCE_STORAGE_KEY)) {
      const currentSourceUrl = new URL(
        `${pathname}${search}${hash}`,
        window.location.origin,
      );
      storeSourceUrl(currentSourceUrl.toString());
    }
    navigate(targetPath);
  };

  const menuDefinitions: Array<{
    label: string;
    onClick: () => void;
    isVisible: (currentPath: string) => boolean;
  }> = [
    {
      label: t("header.menu.userManagement"),
      onClick: () => handleMenuItemClick(userManagementPath),
      isVisible: (currentPath) =>
        canAccessUserManagement && currentPath !== ROUTES.USER_MANAGEMENT,
    },
    {
      label: t("header.menu.settings"),
      onClick: () => handleMenuItemClick(profileSettingsPath),
      isVisible: (currentPath) => currentPath !== ROUTES.PROFILE_SETTINGS,
    },
    {
      label: t("header.menu.signOut"),
      onClick: () => logout({ logoutParams: { returnTo: ENV_APP_BASE_URL } }),
      isVisible: () => true,
    },
  ];

  const menuItems: DropdownMenuItem[] = menuDefinitions
    .filter((item) => item.isVisible(normalizedPathname))
    .map(({ label, onClick }) => ({ label, onClick }));

  // Don't render header in MFA or phone update popup screens
  if (isMfaRoute || isUpdatePhoneRoute) {
    return null;
  }

  return (
    <header className="panelHeader">
      <div
        className="brandBar"
        data-name="Lucid Brand Bar"
        data-node-id="6934:94689"
      >
        <div className="brandBarLeft">
          <img
            className="brandWordmark"
            src={lucidWordmarkLogo}
            alt="Lucid"
            data-node-id="6934:94698"
          />
          <img
            className="brandBear"
            src={lucidBearLogo}
            alt="Lucid bear logo"
            data-node-id="6934:94693"
          />
        </div>
        <div className="headerActionsWrap">
          {isDashboardRoute && (
            <div className="headerSearchWrap">
              <Input
                className="headerSearchInput"
                dataTestId="header-search-input"
                name="headerSearch"
                placeholder={t("header.searchPlaceholder")}
                value={searchValue}
                onChange={(event) => handleSearchChange(event.target.value)}
                size="medium"
                controlled
                theme="dark"
                helperIcon={{
                  category: "global",
                  name: searchValue ? "close" : "search",
                }}
                handleIconClick={
                  searchValue ? () => handleSearchChange("") : undefined
                }
                inputProps={{
                  className: "headerSearchInputField",
                  maxLength: 255,
                }}
                showValidation={false}
              />
            </div>
          )}
          <DropdownMenu
            triggerVariant="user"
            userName={dropdownUserName}
            userSubtitle={organizationName}
            items={menuItems}
            align="right"
            className="headerUserMenu"
            triggerClassName="headerUserMenuTrigger"
            menuClassName="headerUserMenuPanel"
          />
        </div>
      </div>

      {(isProfileSettingsRoute || isUserManagementRoute) && (
        <div className="accountHeader">
          {canGoBack && (
            <Button
              variant="button"
              type="tertiary"
              theme="dark"
              size="large"
              label={t("profileSettings.back")}
              iconLeft={{ category: "arrows", name: "chevron-left" }}
              handleClick={goBack}
              gaComponent="profile-back"
              dataTestId="profile-back-button"
              className="profileBackBtn"
              noPadding
            />
          )}
          <Typography
            as="h2"
            variant="h2"
            theme="light"
            color="black"
            className={
              isUserManagementRoute
                ? "accountHeaderTitle accountHeaderTitleUserManagement"
                : "accountHeaderTitle"
            }
            data-node-id="6934:94708"
          >
            {isUserManagementRoute
              ? organizationName
              : t("profileSettings.title")}
          </Typography>
          <Typography
            as="p"
            variant="body"
            size="medium"
            theme="light"
            color="gray-5"
            className={
              isUserManagementRoute
                ? "accountHeaderSubtitle accountHeaderSubtitleUserManagement"
                : "accountHeaderSubtitle"
            }
          >
            {isUserManagementRoute
              ? t("header.userManagementSubtitle")
              : t("profileSettings.subtitle")}
          </Typography>
        </div>
      )}
    </header>
  );
}
